import { useStore } from '@nanostores/react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useEffect } from 'react'

import initData from '@/services/initData'
import type { InitData, LangCode } from '@/services/schemas'
import useCartStore from '@/stores/CartStore'
import { $country } from '@/stores/Country'
import { $data } from '@/stores/Data'
import { useHubStore } from '@/stores/HubStore'
import { $language } from '@/stores/Language'
import useLimitStore from '@/stores/LimitStore'
import { useMobileLayoutStore } from '@/stores/MobileLayoutStore'

/**
 * Processes parts data to handle OriginalRNumber logic
 * If OriginalRNumber exists and differs from RNumber:
 * - Set RNumber to OriginalRNumber
 * - Set RNumberSpecial to the original RNumber value
 */
function processRNumberLogic(data: InitData): InitData {
  const updatedData: InitData = {
    hubs: data.hubs.map(hub => ({
      ...hub,
      categories: hub.categories.map(category => ({
        ...category,
        parts: category.parts.map((part) => {
          // Check if part has OriginalRNumber and if it's different from RNumber
          if (part.OriginalRNumber && part.OriginalRNumber !== part.RNumber) {
            return {
              ...part,
              RNumber: part.OriginalRNumber, // Set RNumber to the original
              RNumberSpecial: part.RNumber, // Save the "new" one in RNumberSpecial
            }
          }

          return part
        }),
      })),
    })),
  }

  return updatedData
}

export default function useData() {
  const language: LangCode = useStore($language)
  const country = useStore($country)
  const hubInitialize = useHubStore(store => store.initialize)
  const limitInitialize = useLimitStore(store => store.initialize)
  const cartInitialize = useCartStore(store => store.initialize)
  const cartRevalidate = useCartStore(store => store.revalidate)
  const isMobile = useMobileLayoutStore(store => store.isMobile)
  const hubType = useMobileLayoutStore(store => store.activeMobileTab)
  const filterHubs = useHubStore(store => store.filterHubs)

  const { data, isLoading, isError } = useSuspenseQuery({
    queryKey: ['initData', language, country],
    queryFn: async () => {
      const response = await initData({
        lang: language,
        isocode: country ?? 'US',
      })

      // Process RNumber logic based on OriginalRNumber field
      const processedResponse = processRNumberLogic(response)

      return processedResponse
    },
  })

  useEffect(() => {
    if (!data) return

    const dataAtom = $data.get()
    $data.set(data)

    if (!dataAtom) {
      hubInitialize()
      cartInitialize()
      limitInitialize()
    } else {
      cartRevalidate()
      hubInitialize()
      if (isMobile) {
        filterHubs(hubType)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isError])

  return {
    data,
  }
}
